import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { GoabDatePickerOnChangeDetail } from '@abgov/ui-components-common';
import { Utilities } from '@common/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type DateField = 'startDate' | 'endDate';
type TimeControl = 'startHour' | 'startMinute' | 'startMeridiem' | 'endHour' | 'endMinute' | 'endMeridiem';
type Meridiem = 'AM' | 'PM';

interface DropdownOption {
  value: string;
  label: string;
}

const PLACEHOLDER = '__NONE__';

export interface UtcDateTimeRange {
  startUtc: string | null; // ISO string with Z
  endUtc: string | null; // ISO string with Z
}

@Component({
  standalone: false,
  selector: 'd3-datetime-range-picker',
  templateUrl: './datetime-range-picker.component.html',
  styleUrls: ['./datetime-range-picker.component.scss'],
})
export class D3DateTimeRangePickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() startUtc: string | null = null;
  @Input() endUtc: string | null = null;

  // Optional labels
  @Input() startLabel = 'Start date';
  @Input() endLabel = 'End date';
  @Input() startHelperText = '';
  @Input() endHelperText = '';

  // Emits whenever the user changes the range (NOT during init / input patching)
  @Output() rangeChange = new EventEmitter<UtcDateTimeRange>();

  readonly PLACEHOLDER = PLACEHOLDER;

  private destroy$ = new Subject<void>();
  private didInit = false;
  private isPatchingFromInputs = false;
  private lastEmittedKey: string | null = null;

  hours12Options: DropdownOption[] = [
    { value: PLACEHOLDER, label: 'Hour' },
    ...Array.from({ length: 12 }, (_, i) => {
      const v = String(i + 1).padStart(2, '0');
      return { value: v, label: v };
    }),
  ];

  minuteOptions: DropdownOption[] = [
    { value: PLACEHOLDER, label: 'Min' },
    ...Array.from({ length: 4 }, (_, i) => {
      const v = String(i * 15).padStart(2, '0'); // 00, 15, 30, 45
      return { value: v, label: v };
    }),
  ];

  meridiemOptions: DropdownOption[] = [
    { value: PLACEHOLDER, label: '- - -' },
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
  ];

  form = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    startHour: new FormControl<string>(PLACEHOLDER, { nonNullable: true }),
    startMinute: new FormControl<string>(PLACEHOLDER, { nonNullable: true }),
    startMeridiem: new FormControl<string>(PLACEHOLDER, { nonNullable: true }),

    endDate: new FormControl<Date | null>(null),
    endHour: new FormControl<string>(PLACEHOLDER, { nonNullable: true }),
    endMinute: new FormControl<string>(PLACEHOLDER, { nonNullable: true }),
    endMeridiem: new FormControl<string>(PLACEHOLDER, { nonNullable: true }),
  });

  ngOnInit(): void {
    this.resetTimeDefaults();

    // Patch from inputs (no emit)
    this.applyInputsToForm(false);

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.didInit) return;
      if (this.isPatchingFromInputs) return;
      this.emitRangeIfChanged();
    });

    this.didInit = true;

    // Emit once AFTER init/render to seed parent with current UI values
    queueMicrotask(() => this.emitRangeIfChanged());
    // (or setTimeout(() => this.emitRangeIfChanged(), 0); if you prefer)
  }

  private emitRangeIfChanged(): void {
    if (!this.didInit) return;

    const range = this.getUtcRange();
    const key = `${range.startUtc ?? ''}|${range.endUtc ?? ''}`;

    if (key === this.lastEmittedKey) return;
    this.lastEmittedKey = key;

    this.rangeChange.emit(range);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If inputs change after init (e.g., load from API), update the form (DO NOT emit)
    if (changes['startUtc'] || changes['endUtc']) {
      // If we're not initialized yet, ngOnInit will do the initial patch
      if (!this.didInit) return;

      this.applyInputsToForm(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---- Public API for parent (method call) ----
  getUtcRange(): UtcDateTimeRange {
    const startCombined = this.combineDateAndTime(
      this.form.controls.startDate.value,
      this.form.controls.startHour.value,
      this.form.controls.startMinute.value,
      this.form.controls.startMeridiem.value
    );

    const endCombined = this.combineDateAndTime(
      this.form.controls.endDate.value,
      this.form.controls.endHour.value,
      this.form.controls.endMinute.value,
      this.form.controls.endMeridiem.value
    );

    return {
      startUtc: startCombined ? Utilities.utcTime(startCombined) : null,
      endUtc: endCombined ? Utilities.utcTime(endCombined) : null,
    };
  }

  clearStart(): void {
    this.form.controls.startDate.setValue(null);
    this.clearStartTimeOnly();
    // this.emitNow();
  }

  clearEnd(): void {
    this.form.controls.endDate.setValue(null);
    this.clearEndTimeOnly();
    // this.emitNow();
  }

  // ---- UI Handlers ----
  onDateChange(event: GoabDatePickerOnChangeDetail, field: DateField): void {
    const date = (event?.value as Date | undefined) ?? null;

    this.form.controls[field].setValue(date);

    if (!date) {
      field === 'startDate' ? this.clearStartTimeOnly() : this.clearEndTimeOnly();
    }

    // this.emitNow();
  }

  onDropdownChange(event: any, controlName: TimeControl): void {
    const rawValue = event?.value ?? event?.detail?.value ?? event?.target?.value ?? PLACEHOLDER;
    this.form.controls[controlName].setValue(String(rawValue));
    // this.emitNow();
  }

  // ---- Internals ----
  private emitNow(): void {
    // Only emit after init; prevents NG0100 during initial render
    if (!this.didInit) return;
    this.rangeChange.emit(this.getUtcRange());
  }

  private resetTimeDefaults(): void {
    this.clearStartTimeOnly();
    this.clearEndTimeOnly();
  }

  private clearStartTimeOnly(): void {
    this.form.controls.startHour.setValue(PLACEHOLDER);
    this.form.controls.startMinute.setValue(PLACEHOLDER);
    this.form.controls.startMeridiem.setValue(PLACEHOLDER);
  }

  private clearEndTimeOnly(): void {
    this.form.controls.endHour.setValue(PLACEHOLDER);
    this.form.controls.endMinute.setValue(PLACEHOLDER);
    this.form.controls.endMeridiem.setValue(PLACEHOLDER);
  }

  private toNullIfPlaceholder(v: string): string | null {
    return v === PLACEHOLDER ? null : v;
  }

  private combineDateAndTime(
    date: Date | null,
    hour12Str: string,
    minuteStr: string,
    meridiemStr: string
  ): Date | null {
    const hour = this.toNullIfPlaceholder(hour12Str);
    const minute = this.toNullIfPlaceholder(minuteStr);
    const meridiem = this.toNullIfPlaceholder(meridiemStr) as Meridiem | null;

    if (!date || !hour || !minute || !meridiem) return null;

    const hour12 = Number(hour);
    const min = Number(minute);

    if (!Number.isFinite(hour12) || hour12 < 1 || hour12 > 12) return null;
    if (!Number.isFinite(min) || min < 0 || min > 59) return null;

    let hour24 = hour12 % 12;
    if (meridiem === 'PM') hour24 += 12;

    const combined = new Date(date);
    combined.setHours(hour24, min, 0, 0);
    return combined;
  }

  /**
   * Patch the form from incoming inputs.
   * IMPORTANT: Do NOT emit while doing this, otherwise parent can hit NG0100.
   */
  private applyInputsToForm(emitAfterPatch: boolean): void {
    this.isPatchingFromInputs = true;

    try {
      this.applySingleInput(this.startUtc, 'start');
      this.applySingleInput(this.endUtc, 'end');
    } finally {
      this.isPatchingFromInputs = false;
    }

    if (emitAfterPatch) {
      this.emitNow();
    }
  }

  private applySingleInput(valueUtc: string | null, prefix: 'start' | 'end'): void {
    // CLEAR UI when input is null
    if (!valueUtc) {
      if (prefix === 'start') {
        this.form.controls.startDate.setValue(null);
        this.clearStartTimeOnly();
      } else {
        this.form.controls.endDate.setValue(null);
        this.clearEndTimeOnly();
      }
      return;
    }

    const d = new Date(valueUtc);
    if (isNaN(d.getTime())) return;

    const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const hour24 = d.getHours();
    const minute = d.getMinutes();

    const minuteRounded = Math.round(minute / 15) * 15;
    const safeMinute = minuteRounded === 60 ? 45 : minuteRounded;
    const minuteStr = String(safeMinute).padStart(2, '0');

    const meridiem: Meridiem = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const hourStr = String(hour12).padStart(2, '0');

    if (prefix === 'start') {
      this.form.controls.startDate.setValue(dateOnly);
      this.form.controls.startHour.setValue(hourStr);
      this.form.controls.startMinute.setValue(minuteStr);
      this.form.controls.startMeridiem.setValue(meridiem);
    } else {
      this.form.controls.endDate.setValue(dateOnly);
      this.form.controls.endHour.setValue(hourStr);
      this.form.controls.endMinute.setValue(minuteStr);
      this.form.controls.endMeridiem.setValue(meridiem);
    }
  }
}
